import { COLORS } from './colors';

/**
 * Combat HUD layout + timing (docs/06, combat HUD section). All rows are offsets
 * inside the left HUD column, which sits at the TV safe-area margin and is
 * scaled down on small (phone) viewports.
 */
export const HUD = {
  /** Minimum safe-area margin (px) */
  baseMargin: 24,
  /** Margin as a share of the shorter screen edge (TV overscan) */
  safeAreaPct: 0.035,
  /** Left column scale = clamp(min(w/refW, h/refH), minScale, 1) */
  refW: 1280,
  refH: 720,
  minScale: 0.74,

  /** Left column rows (y offsets, unscaled px) */
  rows: {
    hp: 4,
    energy: 44,
    dash: 62,
    gemYellow: 92,
    gemRed: 122,
    level: 152,
    weapons: 172,
  },

  /** "S1 · ROOM 3/7" readout on the level row, right of "LV n" (sector runs) */
  sectorTag: { x: 84, size: 13 },

  /** Minimum left-column width (unscaled) — covers the SUPER READY label */
  leftColumnW: 290,

  /**
   * Top-centre band (boss bar + notices) lives between the left column and
   * a right reserve (touch pause button). Centred on screen when it fits
   * at least `minCenteredW`, else centred in the free region (phones).
   */
  topBand: {
    gap: 16,
    rightReserve: 90,
    minCenteredW: 480,
  },

  bossBar: {
    maxW: 560,
    widthPct: 0.5,
    h: 18,
    /** Boss name baseline below the bar top */
    nameOffset: 48,
  },

  energy: {
    w: 160,
    h: 12,
    /** Gap between a bar and its right-hand label */
    labelGap: 10,
    /** SUPER READY pulse speed (rad/s) and minimum alpha */
    pulseSpeed: 7,
    pulseMinAlpha: 0.45,
    chargeColor: 0x4a7a3a,
    labelDim: 0x7d8a76,
  },

  dash: {
    w: 160,
    h: 6,
    readyColor: COLORS.uiAccent,
    chargeColor: 0x2f6f68,
    labelDim: 0x5c6670,
  },

  weapons: {
    chipW: 64,
    chipH: 66,
    gap: 6,
    /** Glyph icon scale inside a chip (72 px texture → ~40 px glyph) */
    iconScale: 0.66,
    /** Reload bar is only drawn for sockets slower than this (s/shot) —
     *  fast guns would just flicker */
    minReloadShown: 0.25,
    lockedAlpha: 0.38,
    reloadBarH: 4,
    /** Socket number badge (hex radius / digit size) — same 1-2-3 as the shop */
    badgeR: 13,
    badgeSize: 16,
    /** Bag chip after the sockets: bag glyph + count, sparkle when new */
    bagGlyph: 13,
    sparklePulse: 5,
    /** Pickup toast under the strip: glyph → where it went (docs/09 wave 4) */
    toast: { y: 76, w: 172, h: 54, life: 2.8, pop: 0.18, fade: 0.5, glyphScale: 0.62 },
  },

  notices: {
    /** Lines visible at once in the top-centre band */
    maxVisible: 3,
    /** Waiting messages beyond the visible lines (lowest priority drops) */
    queueCap: 8,
    /** Band top = boss bar top + this offset (below the boss name) */
    bandOffset: 62,
    lineGap: 6,
    fadeIn: 0.12,
    fadeOut: 0.3,
    /** A waiting higher-priority message cuts the oldest line to this */
    preemptRemaining: 0.25,
    /** Queued news older than this is stale — dropped unseen (s) */
    maxWait: 3.5,
  },

  /** Floating name label above the nearest weapon crate */
  crateLabel: {
    radius: 260,
    offsetY: -50,
    fontSize: 13,
    /** Wordless "→ destination" bubble above the name line (docs/09 wave 4) */
    bubbleW: 92,
    bubbleH: 40,
    digitSize: 17,
  },

  /** Lifetime bests watched for the one-time in-run "★ record" toast */
  recordToastMetrics: ['timeAlive', 'bossKills', 'gemsCollected'],
  /** Xbox Edge: no pad talking after this long (ms) → show the setup hint */
  xboxHintDelay: 5000,
} as const;

export type NoticeKind = 'boss' | 'portal' | 'wave' | 'pickup' | 'level' | 'record';

export interface NoticeStyle {
  /** Higher shows first and may cut lower lines short */
  priority: number;
  fontSize: number;
  /** Seconds fully visible (before fade-out) */
  hold: number;
  color: number;
}

export const NOTICE_STYLE: Record<NoticeKind, NoticeStyle> = {
  boss: { priority: 4, fontSize: 30, hold: 1.8, color: COLORS.dangerRed },
  portal: { priority: 3, fontSize: 28, hold: 1.8, color: COLORS.uiAccent },
  wave: { priority: 2, fontSize: 26, hold: 1.3, color: COLORS.playerYellow },
  pickup: { priority: 2, fontSize: 22, hold: 2.0, color: COLORS.playerCream },
  level: { priority: 1, fontSize: 20, hold: 1.6, color: COLORS.playerCream },
  record: { priority: 1, fontSize: 20, hold: 1.6, color: COLORS.gemYellow },
};
